import { ManagedReceivingPartnerApi as Api } from "@capstone/mock-api";
import { Day } from "src/app/core/day.model";
import { formatDay } from "src/app/core/pipes/day.pipe";
import { formatTimeOfDay } from "src/app/core/pipes/time-of-day.pipe";
import { formatWeekdays } from "src/app/core/pipes/weekdays.pipe";
import { Site } from "src/app/core/sites";
import { TimeOfDay } from "src/app/core/time-of-day.model";
import { Weekdays } from "src/app/core/weekdays.model";
import { getApiDetailsDecorator, getUpdatedPropertiesFilter } from "src/utils";
import { isExistent } from "src/utils/miscellaneous";

const api = getApiDetailsDecorator<Api.DockCutoffTimeException>();

abstract class BaseScheduleCutoffTimeException {
  protected constructor(
    args: ClassProperties<BaseScheduleCutoffTimeException>,
  ) {
    this.customName = args.customName;
    this.cutoffTime = args.cutoffTime;
    this.endDay = args.endDay;
    this.site = args.site;
    this.startDay = args.startDay;
    this.weekdays = args.weekdays;
  }

  @api({ key: "name" }) public readonly customName: string | null;
  @api({ key: "dayOfWeek" }) public readonly weekdays: Weekdays;
  @api({ key: "endDate" }) public readonly endDay: Day;
  @api({ key: "scheduleCutoffTime" }) public readonly cutoffTime: TimeOfDay;
  public readonly site: Site;
  @api({ key: "startDate" }) public readonly startDay: Day;
}

export class ScheduleCutoffTimeException extends BaseScheduleCutoffTimeException {
  private constructor(
    args: Omit<ClassProperties<ScheduleCutoffTimeException>, "name">,
  ) {
    super(args);
    this.id = args.id;
    this.name = this.customName || formatScheduleCutoffExceptionName(this);
  }

  public readonly id: number;
  public readonly name: string;

  public static deserialize(
    data: Api.DockCutoffTimeException,
    { site }: DeserializeArguments,
  ): ScheduleCutoffTimeException {
    if (!data.startDate) {
      throw new Error(
        "Start date required for schedule cutoff time exception.",
      );
    }
    if (!data.endDate) {
      throw new Error("End date required for schedule cutoff time exception.");
    }

    return new ScheduleCutoffTimeException({
      customName: data.name ?? null,
      cutoffTime: TimeOfDay.deserialize(data.scheduleCutoffTime),
      endDay: Day.deserialize(data.endDate, site.timeZone),
      id: data.id,
      site,
      startDay: Day.deserialize(data.startDate, site.timeZone),
      weekdays: Weekdays.deserialize(data.dayOfWeek),
    });
  }
}

export class ScheduleCutoffTimeExceptionUpdate extends BaseScheduleCutoffTimeException {
  public constructor({
    base,
    ...args
  }: ScheduleCutoffTimeExceptionUpdateArguments) {
    super(args);

    this.base = base;
    this.filterUpdatedProperties = getUpdatedPropertiesFilter(this, base, args);
  }

  public readonly base: ScheduleCutoffTimeException | null;
  private readonly filterUpdatedProperties: FilterPartial<Api.DockScheduleCutoffTimeExceptionUpdate>;

  public serialize(): Partial<Api.HourlyDockCapacityUpdate> {
    const dayOfWeek = this.weekdays.serialize();
    if (!dayOfWeek) {
      throw new Error(
        "Schedule cutoff time exceptions must have at least one selected day.",
      );
    }

    return this.filterUpdatedProperties({
      dayOfWeek,
      endDate: this.endDay.toString(),
      name: this.customName,
      scheduleCutoffTime: this.cutoffTime.serialize(),
      startDate: this.startDay.toString(),
    });
  }
}

export type ScheduleCutoffTimeExceptionUpdateArguments =
  ClassProperties<ScheduleCutoffTimeExceptionUpdate>;

interface DeserializeArguments {
  readonly site: Site;
}

export function formatScheduleCutoffExceptionName({
  weekdays,
  cutoffTime,
  startDay,
  endDay,
}: BaseScheduleCutoffTimeException): string {
  const formattedWeekdays = formatWeekdays(weekdays, "short");
  const formattedStart = formatTimeOfDay(cutoffTime, "hour");
  const formattedStartDay = formatDay(startDay, "en-US", "short");
  const formattedEndDay = formatDay(endDay, "en-US", "short");

  return [
    `${formattedWeekdays} ${formattedStart}`,
    formattedStartDay,
    formattedEndDay,
  ]
    .filter(isExistent)
    .join(" | ");
}
